import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import NotificationsBell from "./NotificationsBell";
import UserSearch from "./UserSearch";
import GoLiveModal from "./GoLiveModal";
import LanguageSwitcher from "./LanguageSwitcher";
import {
    HouseLine, Trophy, FireSimple, Ranking, User, Wallet, UploadSimple, SignOut, ShieldStar, Broadcast, GearSix, X, Camera, Bell, ChatCircle,
} from "@phosphor-icons/react";

export default function Layout() {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [goLiveOpen, setGoLiveOpen] = useState(false);
    const [unreadMsgs, setUnreadMsgs] = useState(0);
    // Hide global mobile chrome on the immersive live-stream page so it doesn't overlay the in-stream header
    const isLiveStreamPage = /^\/live\/[^/]+/.test(location.pathname);

    const navItems = [
        { to: "/feed", label: t("nav.feed"), icon: HouseLine, testid: "nav-feed" },
        { to: "/competitions", label: t("nav.competitions"), icon: Trophy, testid: "nav-competitions" },
        { to: "/trending", label: "Trending", icon: FireSimple, testid: "nav-trending" },
        { to: "/live", label: t("nav.live"), icon: Broadcast, testid: "nav-live" },
        { to: "/leaderboard", label: t("nav.leaderboard"), icon: Ranking, testid: "nav-leaderboard" },
    ];

    // Poll unread message count every 20s for logged-in users
    useEffect(() => {
        if (!user) { setUnreadMsgs(0); return; }
        let stop = false;
        const load = async () => {
            try {
                const { data } = await api.get("/messages/unread-count");
                if (!stop) setUnreadMsgs(data.unread || 0);
            } catch { /* silent */ }
        };
        load();
        const t = setInterval(load, 20000);
        return () => { stop = true; clearInterval(t); };
    }, [user, location.pathname]);

    const onLogout = async () => {
        setSettingsOpen(false);
        await logout();
        nav("/login");
    };
    const closeAndGo = (to) => { setSettingsOpen(false); nav(to); };

    return (
        <div className="min-h-screen bg-wta-bg text-white flex">
            {/* Side nav (desktop) */}
            <aside className="hidden md:flex w-64 flex-col border-r border-wta-border bg-wta-surface sticky top-0 h-screen z-40">
                <div className="px-4 py-4 border-b border-wta-border flex items-center justify-between gap-3">
                    <Link to="/feed" data-testid="logo-link">
                        <img src="/wta-logo.png" alt="World Talent Arena" className="h-12 w-auto" />
                    </Link>
                    <div className="flex items-center">
                        <UserSearch />
                        <NotificationsBell />
                    </div>
                </div>

                <nav className="flex-1 py-4">
                    {navItems.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            data-testid={it.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-6 py-3 text-sm transition-all ${
                                    isActive ? "text-wta-primary border-l-2 border-wta-primary bg-white/5" : "text-white/70 hover:text-white"
                                }`
                            }
                        >
                            <it.icon size={20} weight={it.label === "Live" ? "fill" : "regular"} />
                            <span className="font-heading text-lg tracking-wider">{it.label}</span>
                        </NavLink>
                    ))}
                    {user && (
                        <>
                            <NavLink to={`/profile/${user.id}`} data-testid="nav-profile" className={({ isActive }) =>
                                `flex items-center gap-3 px-6 py-3 text-sm transition-all ${isActive ? "text-wta-primary border-l-2 border-wta-primary bg-white/5" : "text-white/70 hover:text-white"}`
                            }>
                                <User size={20} />
                                <span className="font-heading text-lg tracking-wider">{t("nav.profile")}</span>
                            </NavLink>
                            <NavLink to="/messages" data-testid="nav-messages" className={({ isActive }) =>
                                `flex items-center gap-3 px-6 py-3 text-sm transition-all relative ${isActive ? "text-wta-primary border-l-2 border-wta-primary bg-white/5" : "text-white/70 hover:text-white"}`
                            }>
                                <ChatCircle size={20} />
                                <span className="font-heading text-lg tracking-wider">{t("nav.messages")}</span>
                                {unreadMsgs > 0 && (
                                    <span data-testid="nav-messages-badge" className="ml-auto bg-wta-primary text-white text-[10px] px-1.5 py-0.5 rounded-full font-heading tracking-wider">
                                        {unreadMsgs > 99 ? "99+" : unreadMsgs}
                                    </span>
                                )}
                            </NavLink>
                            <NavLink to="/wallet" data-testid="nav-wallet" className={({ isActive }) =>
                                `flex items-center gap-3 px-6 py-3 text-sm transition-all ${isActive ? "text-wta-primary border-l-2 border-wta-primary bg-white/5" : "text-white/70 hover:text-white"}`
                            }>
                                <Wallet size={20} />
                                <span className="font-heading text-lg tracking-wider">{t("nav.wallet")}</span>
                            </NavLink>
                            <NavLink to="/upload" data-testid="nav-upload" className={({ isActive }) =>
                                `flex items-center gap-3 px-6 py-3 text-sm transition-all ${isActive ? "text-wta-primary border-l-2 border-wta-primary bg-white/5" : "text-white/70 hover:text-white"}`
                            }>
                                <UploadSimple size={20} />
                                <span className="font-heading text-lg tracking-wider">{t("nav.upload")}</span>
                            </NavLink>
                            {user.role === "admin" && (
                                <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) =>
                                    `flex items-center gap-3 px-6 py-3 text-sm transition-all ${isActive ? "text-wta-primary border-l-2 border-wta-primary bg-white/5" : "text-white/70 hover:text-white"}`
                                }>
                                    <ShieldStar size={20} />
                                    <span className="font-heading text-lg tracking-wider">{t("nav.admin")}</span>
                                </NavLink>
                            )}
                        </>
                    )}
                </nav>

                <div className="p-4 border-t border-wta-border">
                    {user ? (
                        <div className="space-y-2">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-wta-surface2 border border-wta-border flex items-center justify-center font-heading text-lg overflow-hidden">
                                    {user.profile_pic ? <img src={user.profile_pic} alt="" className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{user.name}</div>
                                    <div className="text-xs text-wta-muted truncate">{user.coins} coins</div>
                                </div>
                            </div>
                            <button
                                data-testid="settings-btn"
                                onClick={() => setSettingsOpen(true)}
                                title="Settings"
                                className="w-full btn-ghost flex items-center justify-center gap-2 py-2 rounded-sm text-xs font-heading uppercase tracking-wider hover:text-wta-primary hover:border-wta-primary"
                            >
                                <GearSix size={14} weight="bold" /> Settings
                            </button>
                        </div>
                    ) : (
                        <Link to="/login" data-testid="login-link" className="block w-full btn-primary text-center py-2.5 px-4 text-base">
                            {t("nav.login")}
                        </Link>
                    )}
                </div>
            </aside>

            <main className={`flex-1 min-w-0 ${isLiveStreamPage ? "" : "pb-20 md:pb-0"}`}>
                {/* Mobile top bar with bell + settings (hidden on full-screen live page) */}
                {!isLiveStreamPage && (
                    <header className="md:hidden sticky top-0 z-40 glass border-b border-wta-border flex items-center justify-between px-4 h-12">
                        <Link to="/feed" data-testid="mobile-logo" className="flex items-center gap-2">
                            <img src="/wta-logo.png" alt="WTA" className="h-8 w-auto" />
                        </Link>
                        <div className="flex items-center gap-1">
                            <UserSearch />
                            <NotificationsBell />
                            {user && (
                                <button
                                    data-testid="mobile-settings-btn"
                                    onClick={() => setSettingsOpen(true)}
                                    aria-label="Settings"
                                    className="text-white/70 hover:text-white p-2"
                                >
                                    <GearSix size={20} weight="bold" />
                                </button>
                            )}
                        </div>
                    </header>
                )}
                <Outlet />
            </main>

            {/* Settings drawer (mobile + desktop) */}
            {user && settingsOpen && (
                <div
                    className="fixed inset-0 z-[60] bg-black/70"
                    onClick={() => setSettingsOpen(false)}
                    data-testid="mobile-settings-overlay"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-0 top-0 bottom-0 w-72 max-w-[85vw] bg-wta-surface border-l border-wta-border flex flex-col"
                        data-testid="mobile-settings-drawer"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-wta-border">
                            <div className="font-heading text-lg tracking-wider uppercase">Settings</div>
                            <button
                                onClick={() => setSettingsOpen(false)}
                                aria-label="Close"
                                data-testid="mobile-settings-close"
                                className="text-white/60 hover:text-white p-1"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-4 py-3 border-b border-wta-border flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-wta-surface2 border border-wta-border flex items-center justify-center font-heading text-lg overflow-hidden shrink-0">
                                {user.profile_pic ? <img src={user.profile_pic} alt="" className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{user.name}</div>
                                <div className="text-xs text-wta-muted truncate">{user.coins} coins · ${(user.earnings || 0).toFixed(0)}</div>
                            </div>
                        </div>

                        <nav className="flex-1 overflow-y-auto py-2">
                            <DrawerItem icon={Broadcast} label={t("live.go_live")} testid="drawer-go-live" onClick={() => { setSettingsOpen(false); setGoLiveOpen(true); }} highlight />
                            <DrawerItem icon={GearSix} label={t("common.settings") !== "common.settings" ? t("common.settings") : "Live Settings"} testid="drawer-live-settings" onClick={() => closeAndGo("/live-settings")} />
                            <DrawerItem icon={User} label={t("nav.profile")} testid="drawer-profile" onClick={() => closeAndGo(`/profile/${user.id}`)} />
                            <DrawerItem icon={ChatCircle} label={t("nav.messages")} testid="drawer-messages" onClick={() => closeAndGo("/messages")} badge={unreadMsgs} />
                            <DrawerItem icon={Wallet} label={t("nav.wallet")} testid="drawer-wallet" onClick={() => closeAndGo("/wallet")} />
                            <DrawerItem icon={UploadSimple} label={t("nav.upload")} testid="drawer-upload" onClick={() => closeAndGo("/upload")} />
                            <DrawerItem icon={Bell} label={t("nav.notifications")} testid="drawer-notifications" onClick={() => closeAndGo("/notifications")} />
                            {!user.voting_eligible && (
                                <DrawerItem icon={Camera} label="Verify Face" testid="drawer-verify-face" onClick={() => closeAndGo("/verify-face")} highlight />
                            )}
                            {user.role === "admin" && (
                                <DrawerItem icon={ShieldStar} label={t("nav.admin")} testid="drawer-admin" onClick={() => closeAndGo("/admin")} />
                            )}

                            {/* Language selector */}
                            <div className="px-4 py-3 border-t border-wta-border mt-2">
                                <div className="text-[10px] tracking-widest uppercase text-wta-muted mb-2">{t("common.language")}</div>
                                <LanguageSwitcher />
                            </div>
                        </nav>

                        <div className="p-4 pb-16 md:pb-20 border-t border-wta-border">
                            <button
                                data-testid="drawer-logout"
                                onClick={onLogout}
                                className="w-full btn-ghost flex items-center justify-center gap-2 py-2.5 rounded-sm text-sm font-heading uppercase tracking-wider hover:text-wta-primary hover:border-wta-primary"
                            >
                                <SignOut size={16} weight="bold" /> {t("nav.logout")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom nav (mobile) */}
            {!isLiveStreamPage && (
                <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass border-t border-wta-border flex justify-around items-center h-16">
                {navItems.map((it) => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        data-testid={`mobile-${it.testid}`}
                        className={({ isActive }) =>
                            `flex flex-col items-center gap-0.5 px-2 ${isActive ? "text-wta-primary" : "text-white/60"}`
                        }
                    >
                        <it.icon size={22} />
                        <span className="text-[10px] font-heading tracking-wider">{it.label}</span>
                    </NavLink>
                ))}
                {user ? (
                    <NavLink to={`/profile/${user.id}`} data-testid="mobile-nav-profile" className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 ${isActive ? "text-wta-primary" : "text-white/60"}`}>
                        <User size={22} />
                        <span className="text-[10px] font-heading tracking-wider">Me</span>
                    </NavLink>
                ) : (
                    <NavLink to="/login" data-testid="mobile-login" className="flex flex-col items-center gap-0.5 px-2 text-white/60">
                        <User size={22} />
                        <span className="text-[10px] font-heading tracking-wider">Login</span>
                    </NavLink>
                )}
            </nav>
            )}

            {/* Go-live modal (shared) */}
            <GoLiveModal
                open={goLiveOpen}
                onClose={() => setGoLiveOpen(false)}
                onLive={(live) => { setGoLiveOpen(false); nav(`/live/${live.id}`); }}
            />
        </div>
    );
}

function DrawerItem({ icon: Icon, label, testid, onClick, highlight = false, badge = 0 }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-white/5 transition ${highlight ? "text-wta-primary" : "text-white/80"}`}
        >
            <Icon size={20} weight={highlight ? "fill" : "regular"} />
            <span className="font-heading text-base tracking-wider flex-1">{label}</span>
            {badge > 0 && (
                <span className="bg-wta-primary text-white text-[10px] px-1.5 py-0.5 rounded-full font-heading tracking-wider">
                    {badge > 99 ? "99+" : badge}
                </span>
            )}
        </button>
    );
}

